/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { CoreErrorKey } from '../key/core-error.key';
import { ApplicationError } from '../model/application-error.model';
import { ObjectAdapter } from '../util/object-adapter.util';

export type EqualsOption = boolean | number | string | string[] | { [key: string]: EqualsOption };

export interface EqualsOptions {
  typeCheck?: EqualsOption;
  excludeUndefined?: EqualsOption;
  deep?: EqualsOption;
  include?: EqualsOption;
  exclude?: EqualsOption;
  arrayOptimization?: EqualsOption;
}

export function equals(x: any, y: any, options?: EqualsOptions): boolean {
  if (!options
    || options.typeCheck === undefined
    || options.deep === undefined
    || options.arrayOptimization === undefined
  ) {
    options = Object.assign({}, options);
    if (options.typeCheck === undefined) {
      options.typeCheck = true;
    }
    if (options.excludeUndefined === undefined) {
      options.excludeUndefined = false;
    }
    if (options.deep === undefined) {
      options.deep = true;
    }
    if (options.arrayOptimization === undefined) {
      options.arrayOptimization = true;
    }
  }
  return internalEquals(x, y, options, new Set(), new Set());
}

function internalEquals(
  x: any, y: any, options: EqualsOptions, visitedX: Set<object>, visitedY: Set<object>
): boolean {
  if (x === y) {
    return true;
  }
  if (!x || !y || typeof x !== 'object' || typeof y !== 'object') {
    return false;
  }
  if (options.typeCheck && x.constructor !== y.constructor) {
    return false;
  }
  if (visitedX.has(x)) {
    throw new ApplicationError({
      key: CoreErrorKey.CIRCULAR_REFERENCE,
      arguments: { key: 'x', value: x },
      message: `Circular reference for parameter 'x' detected`
    });
  }
  if (visitedY.has(y)) {
    throw new ApplicationError({
      key: CoreErrorKey.CIRCULAR_REFERENCE,
      arguments: { key: 'y', value: y },
      message: `Circular reference for parameter 'y' detected`
    });
  }
  visitedX.add(x);
  visitedY.add(y);

  if (options.arrayOptimization && Array.isArray(x) && Array.isArray(y)) {
    // optimization: do not iterate twice over same keys
    // optimization: do not evaluate key options for each index, but only for * (required for compare length)
    if (!options.excludeUndefined && x.length !== y.length) {
      return false;
    }
    const keyOptions = evaluateKeyOptions('*', options);
    if (keyOptions.exclude === true || keyOptions.exclude > 0 || !keyOptions.include) {
      return true;
    }
    if (!options.deep) {
      for (let i = 0; i < x.length; i++) {
        if (x[i] !== y[i]) {
          return false;
        }
      }
    } else {
      for (let i = 0; i < x.length; i++) {
        if (!internalEquals(x[i], y[i], keyOptions, visitedX, visitedY)) {
          return false;
        }
      }
    }
    return true;
  }

  // do not use equals, otherwise equals helper cannot be used in util classes such as observable collection
  // options may are ignored if an object implemnts equals, but does not consider options
  // if (typeof x.equals === 'function') {
  //   return x.equals(y, options) ? true : false;
  // }
  // if (typeof y.equals === 'function') {
  //   return y.equals(x, options) ? true : false;
  // }

  const readerX = ObjectAdapter.from(x);
  const readerY = ObjectAdapter.from(y);
  if (!options.excludeUndefined
    && (options.include === undefined || options.include === true || options.include > 0)
    && (options.exclude === undefined || options.exclude === false || options.exclude === 0)
    && readerX.size !== readerY.size
  ) {
    return false;
  }
  const visitedKeys = new Set<string>();
  for (let key of readerX.keys()) {
    key = key.toString();
    visitedKeys.add(key);
    const keyOptions = evaluateKeyOptions(key, options);
    if (keyOptions.exclude === true || keyOptions.exclude > 0 || !keyOptions.include) {
      continue;
    }
    if (!options.excludeUndefined && !readerY.has(key)) {
      return false;
    }
    if (!options.deep) {
      if (readerX.get(key) === readerY.get(key)) {
        continue;
      }
    } else {
      if (internalEquals(readerX.get(key), readerY.get(key), keyOptions, visitedX, visitedY)) {
        continue;
      }
    }
    return false;
  }
  for (let key of readerY.keys()) {
    key = key.toString();
    if (visitedKeys.has(key)) {
      continue;
    }
    const keyOptions = evaluateKeyOptions(key, options);
    if (keyOptions.exclude === true || keyOptions.exclude > 0 || !keyOptions.include) {
      continue;
    }
    if (!options.excludeUndefined) {
      // assert readerX.has(key) === false
      // x cannot have key, otherwise key would be in visitedKeys
      return false;
    }
    if (readerY.get(key) === undefined) {
      // assert readerX.get(key) === undefined
      // readerX.get(key) must be undefined, otherwise key would be in visitedKeys
      continue;
    }
    return false;
  }
  return true;
}

function evaluateKeyOptions(key: string, options: EqualsOptions): EqualsOptions {
  return {
    typeCheck: evaluteKeyOption(key, options && options.typeCheck, true),
    excludeUndefined: evaluteKeyOption(key, options && options.excludeUndefined, false),
    deep: evaluteKeyOption(key, options && options.deep, true),
    include: evaluteKeyOption(key, options && options.include, true),
    exclude: evaluteKeyOption(key, options && options.exclude, false),
    arrayOptimization: evaluteKeyOption(key, options && options.arrayOptimization, false),
  };
}

function evaluteKeyOption(
  key: string, option: EqualsOption, defaultOption: EqualsOption
): EqualsOption {
  if (option === undefined || option === null) {
    if (defaultOption === undefined || defaultOption === null) {
      return false;
    }
    option = defaultOption;
  }
  if (typeof option === 'boolean') {
    return option;
  }
  if (typeof option === 'number') {
    return option > 1 ? option - 1 : false;
  }
  if (typeof option === 'string') {
    return key === option || option === '*';
  }
  if (Array.isArray(option)) {
    return !!option.find((value) => value === key);
  }
  return option[key] || option['*'];
}
